import React, { Fragment, useEffect, useState } from 'react';
import { Container, Row, Col, Card, CardHeader, CardBody, Table, Button, Spinner, Badge } from 'reactstrap';
import { FileText, ExternalLink } from 'react-feather';
import { toast } from 'react-toastify';
import { Breadcrumbs } from '../../AbstractElements';
import axios from 'axios';
import API_BASE_URL from '../../Config/api';
import authHeader from './authHeader';

const formatDate = (d) => (d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '-');
const formatAmount = (amount, currency) => `${currency === 'USD' ? '$' : '€'}${Number(amount || 0).toFixed(2)}`;

const InvoicesCreditNotes = () => {
  const [invoices, setInvoices] = useState([]);
  const [creditNotes, setCreditNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [invRes, cnRes] = await Promise.all([
          axios.get(`${API_BASE_URL}/api/customer/invoices`, authHeader()),
          axios.get(`${API_BASE_URL}/api/customer/credit-notes`, authHeader()),
        ]);
        setInvoices(invRes.data.invoices || []);
        setCreditNotes(cnRes.data.creditNotes || []);
      } catch (e) {
        console.error('Failed to load invoices / credit notes', e);
        toast.error('Failed to load invoices and credit notes');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Download the invoice PDF as a blob so the JWT auth header is sent, then open it.
  const downloadInvoice = async (orderId) => {
    setDownloading(orderId);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/customer/orders/${orderId}/invoice`,
        { ...authHeader(), responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const win = window.open(url, '_blank');
      if (!win) {
        const a = document.createElement('a');
        a.href = url; a.download = `Invoice_${orderId}.pdf`;
        document.body.appendChild(a); a.click(); a.remove();
      }
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (e) {
      let msg = 'Could not open the invoice. Please try again.';
      if (e.response?.data instanceof Blob) {
        try { msg = JSON.parse(await e.response.data.text()).error || msg; } catch (_) { /* default */ }
      }
      toast.error(msg);
    } finally {
      setDownloading(null);
    }
  };

  const paymentBadge = (inv) => {
    if (inv.is_paid) return <Badge color="success">Paid</Badge>;
    if (Number(inv.total_paid || 0) > 0) return <Badge color="warning">Partially Paid</Badge>;
    return <Badge color="light" className="text-dark">Unpaid</Badge>;
  };

  return (
    <Fragment>
      <Breadcrumbs parent="Dashboard" title="Invoices & Credit Notes" />
      <Container fluid={true}>
        <Row>
          <Col sm="12">
            <Card>
              <CardHeader className="pb-0"><h5>Invoices</h5></CardHeader>
              <CardBody>
                {loading ? (
                  <p className="text-muted">Loading...</p>
                ) : invoices.length === 0 ? (
                  <p className="text-muted">No invoices have been generated yet.</p>
                ) : (
                  <div className="table-responsive">
                    <Table className="table-border-horizontal align-middle">
                      <thead>
                        <tr>
                          <th>Invoice ID</th><th>Store</th><th>Invoice Date</th>
                          <th>Amount</th><th>Payment</th><th>Status</th><th>Document</th>
                        </tr>
                      </thead>
                      <tbody>
                        {invoices.map((inv) => (
                          <tr key={inv.order_id}>
                            <td><strong>{inv.invoice_id || inv.order_id}</strong></td>
                            <td>{inv.store_name || '-'}</td>
                            <td>{formatDate(inv.invoice_date)}</td>
                            <td>{formatAmount(inv.total_amount, inv.currency)}</td>
                            <td>{paymentBadge(inv)}</td>
                            <td>{inv.order_status || '-'}</td>
                            <td>
                              <Button color="primary" size="sm" disabled={downloading === inv.order_id} onClick={() => downloadInvoice(inv.order_id)}>
                                {downloading === inv.order_id ? <Spinner size="sm" /> : <FileText size={14} />}<span className="ms-1">Invoice</span>
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </CardBody>
            </Card>

            <Card>
              <CardHeader className="pb-0"><h5>Credit Notes</h5></CardHeader>
              <CardBody>
                {loading ? (
                  <p className="text-muted">Loading...</p>
                ) : creditNotes.length === 0 ? (
                  <p className="text-muted">You have no credit notes.</p>
                ) : (
                  <div className="table-responsive">
                    <Table className="table-border-horizontal align-middle">
                      <thead>
                        <tr>
                          <th>Credit Note #</th><th>Date</th><th>Amount</th><th>Status</th><th>Document</th>
                        </tr>
                      </thead>
                      <tbody>
                        {creditNotes.map((cn) => (
                          <tr key={cn.id}>
                            <td><strong>{cn.credit_note_number}</strong></td>
                            <td>{formatDate(cn.created_at)}</td>
                            <td>{formatAmount(cn.amount, cn.currency)}</td>
                            <td>
                              {cn.used_for_invoice
                                ? <Badge color="secondary">Applied to {cn.used_for_invoice}</Badge>
                                : <Badge color="success">Available</Badge>}
                            </td>
                            <td>
                              {cn.pdf_url ? (
                                <Button color="primary" size="sm" outline tag="a" href={cn.pdf_url} target="_blank" rel="noopener noreferrer">
                                  <ExternalLink size={14} /><span className="ms-1">View PDF</span>
                                </Button>
                              ) : <span className="text-muted f-12">-</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </Fragment>
  );
};

export default InvoicesCreditNotes;
